import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const COLORS = {
  road: '#1a1a2e',
  yellow: '#f5c518',
  success: '#00b894',
  danger: '#e74c3c',
  warning: '#f39c12',
  lightgrey: '#f8f9fa',
};

function HeatmapLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const heat = L.heatLayer(points, {
      radius: 35,
      blur: 25,
      maxZoom: 17,
      gradient: { 0.2: 'green', 0.5: 'yellow', 0.8: 'orange', 1.0: 'red' }
    }).addTo(map);
    return () => map.removeLayer(heat);
  }, [points, map]);
  return null;
}

function App() {
  const [page, setPage] = useState('signup');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [image, setImage] = useState(null);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [address, setAddress] = useState('');
  const [potholes, setPotholes] = useState([]);
  const [reportMessage, setReportMessage] = useState('');
  const [activeTab, setActiveTab] = useState('report');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showHeatmap, setShowHeatmap] = useState(true);

  useEffect(() => {
    if (token) { fetchPotholes(); fetchStats(); }
  }, [token]);

  const fetchPotholes = async () => {
    const res = await fetch('http://localhost:5000/api/pothole/all');
    const data = await res.json();
    setPotholes(data);
  };

  const fetchStats = async () => {
    const res = await fetch('http://localhost:5000/api/pothole/stats');
    const data = await res.json();
    setStats(data);
  };

  const getLocation = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      setLatitude(position.coords.latitude);
      setLongitude(position.coords.longitude);
      setAddress('Location detected!');
      alert('Location detected successfully!');
    });
  };

  const handleSignup = async () => {
    const res = await fetch('http://localhost:5000/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password })
    });
    const data = await res.json();
    setMessage(data.message);
  };

  const handleLogin = async () => {
    const res = await fetch('http://localhost:5000/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (data.token) {
      localStorage.setItem('token', data.token);
      setToken(data.token);
    } else {
      setMessage(data.message);
    }
  };

  const handleReport = async () => {
    if (!image || !latitude || !longitude) {
      setReportMessage('Please upload image and detect location!');
      return;
    }
    setLoading(true);
    setReportMessage('AI is analyzing your image... please wait!');
    const formData = new FormData();
    formData.append('image', image);
    formData.append('latitude', latitude);
    formData.append('longitude', longitude);
    formData.append('address', address);
    try {
      const res = await fetch('http://localhost:5000/api/pothole/report', { method: 'POST', body: formData });
      const data = await res.json();
      setReportMessage(data.message);
      setLoading(false);
      fetchPotholes();
      fetchStats();
    } catch (error) {
      setReportMessage('Error connecting to server!');
      setLoading(false);
    }
  };

  const handleUpvote = async (id) => {
    await fetch(`http://localhost:5000/api/pothole/upvote/${id}`, { method: 'POST' });
    fetchPotholes();
  };

  const handleStatusChange = async (id, status) => {
    await fetch(`http://localhost:5000/api/pothole/status/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    fetchPotholes();
    fetchStats();
  };

  const handleFixedImage = async (id, file) => {
    const formData = new FormData();
    formData.append('image', file);
    await fetch(`http://localhost:5000/api/pothole/fixedimage/${id}`, { method: 'POST', body: formData });
    fetchPotholes();
    fetchStats();
  };

  const handleShare = (p) => {
    const text = `Pothole Alert!\nSeverity: ${p.severity.toUpperCase()}\nLocation: ${p.location.latitude?.toFixed(4)}, ${p.location.longitude?.toFixed(4)}\nUpvotes: ${p.upvotes || 0}\nReported on RoadSense App!`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setPage('login');
  };

  const getSeverityColor = (s) => s === 'high' ? COLORS.danger : s === 'medium' ? COLORS.warning : COLORS.success;

  if (token) {
    return (
      <div style={{ minHeight: '100vh', background: COLORS.lightgrey }}>

        {/* Header */}
        <div style={{ background: COLORS.road, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', position: 'sticky', top: 0, zIndex: 1000 }}>
          <div style={{ height: '4px', background: `repeating-linear-gradient(90deg, ${COLORS.yellow} 0px, ${COLORS.yellow} 40px, transparent 40px, transparent 80px)` }} />
          <div style={{ padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ fontSize: '32px' }}>🚧</div>
              <div>
                <h1 style={{ color: COLORS.yellow, margin: 0, fontSize: '24px', fontWeight: '900', letterSpacing: '2px' }}>ROADSENSE</h1>
                <p style={{ color: '#888', margin: 0, fontSize: '11px', letterSpacing: '1px' }}>CIVIC ROAD MONITORING SYSTEM</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[{ id: 'report', label: '📸 Report' }, { id: 'map', label: '🗺️ Map' }, { id: 'list', label: '📋 List' }, { id: 'dashboard', label: '🏛️ Dashboard' }].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ background: activeTab === tab.id ? COLORS.yellow : 'transparent', color: activeTab === tab.id ? COLORS.road : '#aaa', border: activeTab === tab.id ? 'none' : '1px solid #444', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', transform: activeTab === tab.id ? 'translateY(-2px)' : 'none', boxShadow: activeTab === tab.id ? '0 4px 12px rgba(245,197,24,0.4)' : 'none' }}>
                  {tab.label}
                </button>
              ))}
              <button onClick={handleLogout} style={{ background: COLORS.danger, color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>🚪 Logout</button>
            </div>
          </div>
          <div style={{ height: '4px', background: `repeating-linear-gradient(90deg, ${COLORS.yellow} 0px, ${COLORS.yellow} 40px, transparent 40px, transparent 80px)` }} />
        </div>

        <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>

          {/* REPORT TAB */}
          {activeTab === 'report' && (
            <div style={{ background: 'white', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              <div style={{ background: COLORS.road, padding: '20px 24px', borderBottom: `4px solid ${COLORS.yellow}` }}>
                <h2 style={{ color: COLORS.yellow, margin: 0, letterSpacing: '1px' }}>📸 REPORT A POTHOLE</h2>
                <p style={{ color: '#888', margin: '4px 0 0', fontSize: '13px' }}>AI-powered severity detection</p>
              </div>
              <div style={{ padding: '24px' }}>
                <div style={{ border: `2px dashed ${COLORS.yellow}`, borderRadius: '12px', padding: '24px', textAlign: 'center', marginBottom: '16px', background: '#fffef0' }}>
                  <p style={{ color: '#888', margin: '0 0 12px', fontSize: '14px' }}>📤 Upload road photo</p>
                  <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} style={{ width: '100%' }} />
                  {image && <p style={{ color: COLORS.success, marginTop: '8px', fontWeight: 'bold' }}>✅ Photo ready!</p>}
                </div>
                <button onClick={getLocation} style={{ width: '100%', padding: '14px', background: COLORS.road, color: COLORS.yellow, border: `2px solid ${COLORS.yellow}`, borderRadius: '12px', fontSize: '16px', cursor: 'pointer', marginBottom: '12px', fontWeight: 'bold' }}>
                  📍 DETECT MY LOCATION
                </button>
                {latitude && (
                  <div style={{ background: '#f0fff4', border: `1px solid ${COLORS.success}`, padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                    <p style={{ color: COLORS.success, margin: 0, fontWeight: 'bold' }}>✅ Location Locked!</p>
                    <p style={{ color: '#666', margin: '4px 0 0', fontSize: '13px' }}>📍 {latitude.toFixed(4)}, {longitude.toFixed(4)}</p>
                  </div>
                )}
                <button onClick={handleReport} disabled={loading} style={{ width: '100%', padding: '16px', background: loading ? '#ccc' : COLORS.yellow, color: loading ? '#888' : COLORS.road, border: 'none', borderRadius: '12px', fontSize: '18px', fontWeight: '900', cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: '2px', boxShadow: loading ? 'none' : '0 6px 20px rgba(245,197,24,0.5)' }}>
                  {loading ? '🤖 AI ANALYZING...' : '🚧 SUBMIT REPORT'}
                </button>
                {reportMessage && (
                  <div style={{ marginTop: '16px', padding: '16px', borderRadius: '12px', background: reportMessage.includes('reported') || reportMessage.includes('successfully') ? '#f0fff4' : '#eff6ff', textAlign: 'center' }}>
                    <p style={{ margin: 0, fontWeight: 'bold', color: reportMessage.includes('reported') || reportMessage.includes('successfully') ? COLORS.success : '#3b82f6' }}>{reportMessage}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MAP TAB */}
          {activeTab === 'map' && (
            <div style={{ background: 'white', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              <div style={{ background: COLORS.road, padding: '20px 24px', borderBottom: `4px solid ${COLORS.yellow}` }}>
                <h2 style={{ color: COLORS.yellow, margin: 0, letterSpacing: '1px' }}>🗺️ LIVE POTHOLE MAP</h2>
                <div style={{ display: 'flex', gap: '16px', marginTop: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ color: COLORS.danger, fontSize: '13px' }}>🔴 {potholes.filter(p => p.severity === 'high').length} High</span>
                  <span style={{ color: COLORS.warning, fontSize: '13px' }}>🟡 {potholes.filter(p => p.severity === 'medium').length} Medium</span>
                  <span style={{ color: COLORS.success, fontSize: '13px' }}>🟢 {potholes.filter(p => p.severity === 'low').length} Low</span>
                  <span style={{ color: '#888', fontSize: '13px' }}>📍 {potholes.length} Total</span>
                  <button onClick={() => setShowHeatmap(!showHeatmap)} style={{ padding: '6px 14px', background: showHeatmap ? COLORS.yellow : 'transparent', color: showHeatmap ? COLORS.road : COLORS.yellow, border: `1px solid ${COLORS.yellow}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
                    {showHeatmap ? '🔥 Heatmap ON' : '📍 Pins Only'}
                  </button>
                </div>
              </div>
              <MapContainer center={[17.3850, 78.4867]} zoom={13} style={{ height: '500px', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
                {showHeatmap && (
                  <HeatmapLayer points={potholes.filter(p => p.location.latitude && p.location.longitude).map(p => [p.location.latitude, p.location.longitude, p.severity === 'high' ? 1.0 : p.severity === 'medium' ? 0.6 : 0.3])} />
                )}
                {potholes.map((p) => (
                  p.location.latitude && p.location.longitude && (
                    <Marker key={p._id} position={[p.location.latitude, p.location.longitude]}>
                      <Popup>
                        <div style={{ minWidth: '180px' }}>
                          <img src={p.image} alt="pothole" style={{ width: '100%', borderRadius: '8px', marginBottom: '8px' }} />
                          {p.fixedImage && (
                            <div>
                              <p style={{ margin: '4px 0', fontSize: '11px', color: COLORS.success, fontWeight: 'bold' }}>✅ AFTER FIX:</p>
                              <img src={p.fixedImage} alt="fixed" style={{ width: '100%', borderRadius: '8px', marginBottom: '8px' }} />
                            </div>
                          )}
                          <p style={{ margin: '4px 0', fontWeight: 'bold', color: getSeverityColor(p.severity) }}>
                            {p.severity === 'high' ? '🔴 High' : p.severity === 'medium' ? '🟡 Medium' : '🟢 Low'} Severity
                          </p>
                          <p style={{ margin: '4px 0', fontSize: '13px' }}>👍 {p.upvotes || 0} upvotes</p>
                          <p style={{ margin: '4px 0', fontSize: '12px', color: '#888' }}>{new Date(p.createdAt).toLocaleDateString()}</p>
                        </div>
                      </Popup>
                    </Marker>
                  )
                ))}
              </MapContainer>
            </div>
          )}

          {/* LIST TAB */}
          {activeTab === 'list' && (
            <div>
              <div style={{ background: 'white', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.1)', overflow: 'hidden', marginBottom: '20px' }}>
                <div style={{ background: COLORS.road, padding: '20px 24px', borderBottom: `4px solid ${COLORS.yellow}` }}>
                  <h2 style={{ color: COLORS.yellow, margin: 0, letterSpacing: '1px' }}>📋 ALL POTHOLES ({potholes.length})</h2>
                </div>
                <div style={{ padding: '16px 24px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: `2px solid ${COLORS.yellow}`, fontSize: '14px', background: COLORS.road, color: COLORS.yellow, fontWeight: 'bold' }}>
                    <option value="all">All Severities</option>
                    <option value="high">🔴 High</option>
                    <option value="medium">🟡 Medium</option>
                    <option value="low">🟢 Low</option>
                  </select>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: `2px solid ${COLORS.yellow}`, fontSize: '14px', background: COLORS.road, color: COLORS.yellow, fontWeight: 'bold' }}>
                    <option value="all">All Status</option>
                    <option value="reported">⚠️ Reported</option>
                    <option value="fixing">🔧 Fixing</option>
                    <option value="fixed">✅ Fixed</option>
                  </select>
                  <button onClick={() => { setFilterSeverity('all'); setFilterStatus('all'); }} style={{ padding: '8px 12px', background: COLORS.danger, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>❌ Clear</button>
                </div>
              </div>

              {potholes
                .filter(p => filterSeverity === 'all' || p.severity === filterSeverity)
                .filter(p => filterStatus === 'all' || p.status === filterStatus)
                .sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0))
                .map((p) => (
                  <div key={p._id} style={{ background: 'white', padding: '16px', borderRadius: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', borderLeft: `5px solid ${getSeverityColor(p.severity)}`, transition: 'transform 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                      <p style={{ margin: 0, fontSize: '9px', color: COLORS.danger, fontWeight: 'bold', textAlign: 'center' }}>BEFORE</p>
                      <img src={p.image} alt="before" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                      {p.fixedImage && (
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ margin: '4px 0 2px', fontSize: '9px', color: COLORS.success, fontWeight: 'bold' }}>AFTER</p>
                          <img src={p.fixedImage} alt="after" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: `2px solid ${COLORS.success}` }} />
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 'bold', color: getSeverityColor(p.severity), fontSize: '16px' }}>
                        {p.severity === 'high' ? '🔴 HIGH' : p.severity === 'medium' ? '🟡 MEDIUM' : '🟢 LOW'} SEVERITY
                      </p>
                      <p style={{ margin: '4px 0', color: '#888', fontSize: '13px' }}>{p.status === 'fixed' ? '✅ Fixed' : p.status === 'fixing' ? '🔧 Being Fixed' : '⚠️ Reported'}</p>
                      <p style={{ margin: '4px 0', color: '#aaa', fontSize: '12px' }}>📍 {p.location.latitude?.toFixed(4)}, {p.location.longitude?.toFixed(4)}</p>
                      {p.aiAnalysis && <p style={{ margin: '4px 0', color: '#6366f1', fontSize: '12px' }}>🤖 {p.aiAnalysis}</p>}
                      <p style={{ margin: '4px 0', color: '#ddd', fontSize: '11px' }}>{new Date(p.createdAt).toLocaleDateString()}</p>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                        <button onClick={() => handleUpvote(p._id)} style={{ padding: '6px 16px', background: COLORS.road, color: COLORS.yellow, border: `1px solid ${COLORS.yellow}`, borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
                          👍 {p.upvotes || 0}
                        </button>
                        <button onClick={() => handleShare(p)} style={{ padding: '6px 16px', background: '#25D366', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
                          📤 WhatsApp
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              {potholes.length === 0 && (
                <div style={{ background: 'white', borderRadius: '20px', padding: '40px', textAlign: 'center' }}>
                  <p style={{ color: '#aaa', fontSize: '18px' }}>No potholes reported yet! 🚧</p>
                </div>
              )}
            </div>
          )}

          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && (
            <div>
              <div style={{ background: 'white', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.1)', overflow: 'hidden', marginBottom: '20px' }}>
                <div style={{ background: COLORS.road, padding: '20px 24px', borderBottom: `4px solid ${COLORS.yellow}` }}>
                  <h2 style={{ color: COLORS.yellow, margin: 0, letterSpacing: '1px' }}>🏛️ GOVERNMENT DASHBOARD</h2>
                  <p style={{ color: '#888', margin: '4px 0 0', fontSize: '13px' }}>Monitor and manage road repairs across Hyderabad</p>
                </div>
              </div>

              {stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                  {[
                    { label: 'Total Reported', value: stats.total, icon: '🚧', bg: COLORS.road, border: COLORS.yellow },
                    { label: 'Being Fixed', value: stats.fixing, icon: '🔧', bg: '#78350f', border: COLORS.warning },
                    { label: 'Fixed', value: stats.fixed, icon: '✅', bg: '#064e3b', border: COLORS.success },
                    { label: 'High Risk', value: stats.high, icon: '🔴', bg: '#7f1d1d', border: COLORS.danger },
                    { label: 'Medium Risk', value: stats.medium, icon: '🟡', bg: '#78350f', border: COLORS.warning },
                    { label: 'Low Risk', value: stats.low, icon: '🟢', bg: '#064e3b', border: COLORS.success },
                  ].map((s, i) => (
                    <div key={i} style={{ background: s.bg, border: `2px solid ${s.border}`, padding: '20px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', transition: 'transform 0.3s', cursor: 'default' }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <h2 style={{ margin: 0, fontSize: '40px', color: s.border }}>{s.value}</h2>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#aaa' }}>{s.icon} {s.label}</p>
                    </div>
                  ))}
                </div>
              )}

              <h3 style={{ color: COLORS.road, marginBottom: '16px' }}>📋 Priority Queue — Highest Upvotes First</h3>

              {potholes
                .sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0))
                .map((p) => (
                  <div key={p._id} style={{ background: 'white', padding: '16px', borderRadius: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', borderLeft: `5px solid ${getSeverityColor(p.severity)}` }}>

                    {/* Before & After Images */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                      <p style={{ margin: 0, fontSize: '9px', color: COLORS.danger, fontWeight: 'bold', textAlign: 'center' }}>BEFORE</p>
                      <img src={p.image} alt="before" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                      {p.fixedImage && (
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ margin: '4px 0 2px', fontSize: '9px', color: COLORS.success, fontWeight: 'bold' }}>AFTER</p>
                          <img src={p.fixedImage} alt="after" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: `2px solid ${COLORS.success}` }} />
                        </div>
                      )}
                    </div>

                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 'bold', color: getSeverityColor(p.severity) }}>
                        {p.severity === 'high' ? '🔴 HIGH' : p.severity === 'medium' ? '🟡 MEDIUM' : '🟢 LOW'} — 👍 {p.upvotes || 0} upvotes
                      </p>
                      <p style={{ margin: '4px 0', color: '#888', fontSize: '13px' }}>📍 {p.location.latitude?.toFixed(4)}, {p.location.longitude?.toFixed(4)}</p>
                      <p style={{ margin: '4px 0', color: '#888', fontSize: '13px' }}>{p.status === 'fixed' ? '✅ Fixed' : p.status === 'fixing' ? '🔧 Being Fixed' : '⚠️ Reported'}</p>
                      {p.aiAnalysis && <p style={{ margin: '4px 0', color: '#6366f1', fontSize: '12px' }}>🤖 {p.aiAnalysis}</p>}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
                      {p.status === 'reported' && (
                        <button onClick={() => handleStatusChange(p._id, 'fixing')} style={{ padding: '8px 14px', background: COLORS.warning, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                          🔧 Mark Fixing
                        </button>
                      )}
                      {p.status === 'fixing' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <input
                            type="file"
                            accept="image/*"
                            id={`fixed-${p._id}`}
                            style={{ display: 'none' }}
                            onChange={(e) => handleFixedImage(p._id, e.target.files[0])}
                          />
                          <button onClick={() => document.getElementById(`fixed-${p._id}`).click()} style={{ padding: '8px 14px', background: COLORS.success, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                            📸 Upload Fixed Photo
                          </button>
                        </div>
                      )}
                      {p.status === 'fixed' && (
                        <span style={{ padding: '8px 14px', background: '#f0fff4', color: COLORS.success, borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', border: `1px solid ${COLORS.success}`, textAlign: 'center' }}>
                          ✅ Done!
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: COLORS.road, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '200px', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ height: '8px', width: '100%', background: `repeating-linear-gradient(90deg, ${COLORS.yellow} 0px, ${COLORS.yellow} 60px, transparent 60px, transparent 120px)` }} />
      </div>
      <div style={{ background: 'white', padding: '40px', borderRadius: '24px', boxShadow: '0 30px 80px rgba(0,0,0,0.5)', width: '100%', maxWidth: '420px', position: 'relative', zIndex: 10, transition: 'transform 0.3s' }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-8px)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
      >
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ fontSize: '48px', marginBottom: '8px' }}>🚧</div>
          <h1 style={{ color: COLORS.road, margin: 0, fontSize: '28px', fontWeight: '900', letterSpacing: '3px' }}>ROADSENSE</h1>
          <p style={{ color: '#888', margin: '8px 0 0', fontSize: '13px', letterSpacing: '1px' }}>CIVIC ROAD MONITORING SYSTEM</p>
          <div style={{ height: '3px', background: `repeating-linear-gradient(90deg, ${COLORS.yellow} 0px, ${COLORS.yellow} 20px, transparent 20px, transparent 40px)`, marginTop: '16px', borderRadius: '2px' }} />
        </div>
        <div style={{ display: 'flex', marginBottom: '24px', background: '#f8f9fa', borderRadius: '12px', padding: '4px' }}>
          <button onClick={() => setPage('signup')} style={{ flex: 1, padding: '10px', background: page === 'signup' ? COLORS.road : 'transparent', color: page === 'signup' ? COLORS.yellow : '#888', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>Sign Up</button>
          <button onClick={() => setPage('login')} style={{ flex: 1, padding: '10px', background: page === 'login' ? COLORS.road : 'transparent', color: page === 'login' ? COLORS.yellow : '#888', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>Login</button>
        </div>
        {page === 'signup' && (
          <div>
            <input placeholder="Your Name" value={name} onChange={(e) => setName(e.target.value)} style={{ width: '93%', padding: '12px 16px', marginBottom: '12px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '16px', outline: 'none' }} onFocus={e => e.target.style.border = `2px solid ${COLORS.yellow}`} onBlur={e => e.target.style.border = '2px solid #e5e7eb'} />
            <input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '93%', padding: '12px 16px', marginBottom: '12px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '16px', outline: 'none' }} onFocus={e => e.target.style.border = `2px solid ${COLORS.yellow}`} onBlur={e => e.target.style.border = '2px solid #e5e7eb'} />
            <input placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ width: '93%', padding: '12px 16px', marginBottom: '16px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '16px', outline: 'none' }} onFocus={e => e.target.style.border = `2px solid ${COLORS.yellow}`} onBlur={e => e.target.style.border = '2px solid #e5e7eb'} />
            <button onClick={handleSignup} style={{ width: '100%', padding: '14px', background: COLORS.road, color: COLORS.yellow, border: `2px solid ${COLORS.yellow}`, borderRadius: '12px', fontSize: '16px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }} onMouseEnter={e => e.target.style.transform = 'translateY(-2px)'} onMouseLeave={e => e.target.style.transform = 'translateY(0)'}>
              CREATE ACCOUNT 🚀
            </button>
          </div>
        )}
        {page === 'login' && (
          <div>
            <input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '93%', padding: '12px 16px', marginBottom: '12px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '16px', outline: 'none' }} onFocus={e => e.target.style.border = `2px solid ${COLORS.yellow}`} onBlur={e => e.target.style.border = '2px solid #e5e7eb'} />
            <input placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ width: '93%', padding: '12px 16px', marginBottom: '16px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '16px', outline: 'none' }} onFocus={e => e.target.style.border = `2px solid ${COLORS.yellow}`} onBlur={e => e.target.style.border = '2px solid #e5e7eb'} />
            <button onClick={handleLogin} style={{ width: '100%', padding: '14px', background: COLORS.road, color: COLORS.yellow, border: `2px solid ${COLORS.yellow}`, borderRadius: '12px', fontSize: '16px', fontWeight: '900', cursor: 'pointer', letterSpacing: '2px' }} onMouseEnter={e => e.target.style.transform = 'translateY(-2px)'} onMouseLeave={e => e.target.style.transform = 'translateY(0)'}>
              LOGIN 🔐
            </button>
          </div>
        )}
        {message && (
          <div style={{ marginTop: '16px', padding: '12px', borderRadius: '10px', background: message.includes('success') || message.includes('created') ? '#f0fff4' : '#fff5f5', border: `1px solid ${message.includes('success') || message.includes('created') ? COLORS.success : COLORS.danger}`, textAlign: 'center' }}>
            <p style={{ margin: 0, fontWeight: 'bold', color: message.includes('success') || message.includes('created') ? COLORS.success : COLORS.danger }}>{message}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;